import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFeedback, FeedbackForm } from './FeedbackContext';
import { useBranches } from './BranchesContext';
import { useCustomerAuth } from './CustomerAuthContext';
export function CustomerFeedbackPage() {
  const {
    forms,
    addResponse
  } = useFeedback();
  const {
    branchId
  } = useBranches();
  const {
    customer
  } = useCustomerAuth();
  const navigate = useNavigate();
  // Find the first active form
  const activeForm = forms.find(f => f.active);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitted, setSubmitted] = useState(false);
  if (!activeForm) {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center" data-id="element-1623">
        <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1624">
          No Feedback Form Available
        </h2>
        <p className="text-gray-500 mb-6" data-id="element-1625">
          We are not currently collecting feedback.
        </p>
        <button onClick={() => navigate(-1)} className="px-6 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors" data-id="element-1626">
          Go Back
        </button>
      </div>;
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate required fields
    const missing = activeForm.fields.filter(f => f.required && !answers[f.id]);
    if (missing.length > 0) {
      alert('Please fill out all required fields.');
      return;
    }
    addResponse({
      formId: activeForm.id,
      branchId,
      customerPhone: customer?.phone,
      answers
    });
    setSubmitted(true);
  };
  if (submitted) {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center" data-id="element-1627">
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 mx-auto" data-id="element-1628">
          <CheckCircle2 size={40} data-id="element-1629" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1630">Thank You!</h2>
        <p className="text-gray-500 mb-8 max-w-sm" data-id="element-1631">
          Your feedback has been submitted successfully. We appreciate your
          input!
        </p>
        <button onClick={() => navigate('/welcome')} className="px-8 py-4 bg-green-600 text-white font-bold text-lg rounded-xl shadow-lg hover:bg-green-700 active:scale-[0.99] transition-all" data-id="element-1632">
          Return Home
        </button>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1633">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-1634">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-4" data-id="element-1635">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1636">
            <ArrowLeft size={24} data-id="element-1637" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 truncate" data-id="element-1638">
            {activeForm.name}
          </h1>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto p-4 sm:p-6" data-id="element-1639">
        <form onSubmit={handleSubmit} className="space-y-8 bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-1640">
          {activeForm.fields.map(field => <div key={field.id} className="space-y-3" data-id="element-1641">
              <label className="block text-base font-bold text-gray-900" data-id="element-1642">
                {field.label}
                {field.required && <span className="text-red-500 ml-1" data-id="element-1643">*</span>}
              </label>

              {field.type === 'rating' && <div className="flex items-center gap-2" data-id="element-1644">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setAnswers({
              ...answers,
              [field.id]: star
            })} className={`p-2 transition-transform hover:scale-110 ${(answers[field.id] || 0) >= star ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-1645">
                      <Star size={36} fill="currentColor" data-id="element-1646" />
                    </button>)}
                </div>}

              {field.type === 'text' && <textarea value={answers[field.id] || ''} onChange={e => setAnswers({
            ...answers,
            [field.id]: e.target.value
          })} rows={4} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none resize-none" placeholder="Type your answer here..." data-id="element-1647" />}

              {field.type === 'single-select' && field.options && <div className="flex flex-col gap-2" data-id="element-1648">
                  {field.options.map(opt => <label key={opt} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${answers[field.id] === opt ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-200'}`} data-id="element-1649">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${answers[field.id] === opt ? 'border-green-600' : 'border-gray-300'}`} data-id="element-1650">
                        {answers[field.id] === opt && <div className="w-2.5 h-2.5 rounded-full bg-green-600" data-id="element-1651" />}
                      </div>
                      <span className={`font-medium ${answers[field.id] === opt ? 'text-green-900' : 'text-gray-700'}`} data-id="element-1652">
                        {opt}
                      </span>
                    </label>)}
                </div>}
            </div>)}

          <div className="pt-6 border-t border-gray-100" data-id="element-1653">
            <button type="submit" className="w-full py-4 bg-green-600 text-white font-bold text-lg rounded-xl shadow-lg hover:bg-green-700 active:scale-[0.99] transition-all" data-id="element-1654">
              Submit Feedback
            </button>
          </div>
        </form>
      </main>
    </div>;
}